import React, { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [currentSlide, setCurrentSlide] = useState(0);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  // Dashboard pages data for slider
  const dashboardPages = [
    {
      title: 'Dashboard Overview',
      description: 'Get a comprehensive view of your account statistics, recent activities, and quick access to all features.',
      image: '/api/placeholder/600/400',
      features: ['Real-time statistics', 'Quick actions', 'Activity feed', 'Account overview']
    },
    {
      title: 'IMEI Check',
      description: 'Instantly verify device information with our advanced IMEI checking system.',
      image: '/api/placeholder/600/400',
      features: ['Instant verification', 'Detailed reports', 'History tracking', 'Bulk checking']
    },
    {
      title: 'Orders Management',
      description: 'Track and manage all your service orders with advanced filtering and search capabilities.',
      image: '/api/placeholder/600/400',
      features: ['Order tracking', 'Status updates', 'Download receipts', 'Filter & search']
    },
    {
      title: 'Invoices & Billing',
      description: 'Manage your billing information and download invoices with our comprehensive billing system.',
      image: '/api/placeholder/600/400',
      features: ['Invoice management', 'Payment history', 'Auto-billing', 'Tax reports']
    },
    {
      title: 'Subscription Plans',
      description: 'Choose and manage your subscription plans with flexible options for every business need.',
      image: '/api/placeholder/600/400',
      features: ['Flexible plans', 'Usage tracking', 'Plan upgrades', 'Custom solutions']
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % dashboardPages.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [dashboardPages.length]);

  return (
    <section id="features" className="w-full py-16 lg:py-24 bg-white" ref={ref}>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
        {/* Features Overview Section */}
        <motion.div 
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            className="text-3xl lg:text-5xl font-bold mb-4 tracking-wide text-gray-900"
            variants={itemVariants}
          >
            TAKE A QUICK OVERVIEW
          </motion.h2>
          <motion.h3 
            className="text-2xl lg:text-4xl font-bold mb-8 tracking-wide text-primary-600"
            variants={itemVariants}
          >
            OF OUR FEATURES
          </motion.h3>
          
          {/* Dashboard Pages Slider */}
          <motion.div 
            className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl overflow-hidden shadow-2xl"
            variants={itemVariants}
          >
            <div className="relative h-96 lg:h-[500px]">
              {/* Slider Content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full max-w-6xl mx-auto px-8">
                  <div className="grid lg:grid-cols-2 gap-8 items-center">
                    {/* Left Side - Content */}
                    <motion.div 
                      key={currentSlide}
                      className="text-left"
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <h4 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                        {dashboardPages[currentSlide].title}
                      </h4>
                      <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                        {dashboardPages[currentSlide].description}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {dashboardPages[currentSlide].features.map((feature, index) => (
                          <li key={index} className="text-gray-700">
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </motion.div>

                    {/* Right Side - Dashboard Preview */}
                    <motion.div 
                      key={`preview-${currentSlide}`}
                      className="relative"
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                        {/* Browser Header */}
                        <div className="bg-gray-100 px-4 py-3 flex items-center space-x-2">
                          <div className="flex space-x-2">
                            <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                            <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                            <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                          </div>
                          <div className="flex-1 bg-white rounded px-3 py-1 text-sm text-gray-500">
                            devicelink.global/dashboard
                          </div>
                        </div>
                        
                        {/* Dashboard Content */}
                        <div className="p-6 h-64">
                          {currentSlide === 0 && (
                            <div className="space-y-4">
                              <div className="grid grid-cols-3 gap-3">
                                <div className="bg-primary-100 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-primary-600">2.5M+</div>
                                  <div className="text-xs text-gray-600">Checks</div>
                                </div>
                                <div className="bg-success-100 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-success-600">$1.2K</div>
                                  <div className="text-xs text-gray-600">Revenue</div>
                                </div>
                                <div className="bg-warning-100 p-3 rounded-lg text-center">
                                  <div className="text-2xl font-bold text-warning-600">15</div>
                                  <div className="text-xs text-gray-600">Pending</div>
                                </div>
                              </div>
                              <div className="bg-gradient-to-r from-primary-500 to-blue-500 h-20 rounded-lg flex items-center justify-center text-white">
                                📊 Analytics Chart
                              </div>
                            </div>
                          )}
                          
                          {currentSlide === 1 && (
                            <div className="space-y-4">
                              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <div className="font-mono text-sm mb-2">358240051111110</div>
                                <div className="text-primary-600 font-medium">iPhone 15 Pro Max</div>
                                <div className="text-sm text-gray-600">Status: Clean ✓</div>
                              </div>
                              <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                                <div className="text-success-600 font-medium">✓ Device Verified</div>
                                <div className="text-sm text-gray-600">Carrier: Unlocked</div>
                              </div>
                            </div>
                          )}
                          
                          {currentSlide === 2 && (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                                  <span className="text-sm">ORD-2025-001</span>
                                </div>
                                <span className="text-sm font-medium">$2.50</span>
                              </div>
                              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <div className="w-2 h-2 bg-warning-500 rounded-full"></div>
                                  <span className="text-sm">ORD-2025-002</span>
                                </div>
                                <span className="text-sm font-medium">$15.00</span>
                              </div>
                            </div>
                          )}
                          
                          {currentSlide === 3 && (
                            <div className="space-y-4">
                              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">Invoice #001</span>
                                  <span className="text-success-600">Paid</span>
                                </div>
                                <div className="text-sm text-gray-600">Amount: $55.00</div>
                              </div>
                              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">Invoice #002</span>
                                  <span className="text-warning-600">Pending</span>
                                </div>
                                <div className="text-sm text-gray-600">Amount: $143.75</div>
                              </div>
                            </div>
                          )}
                          
                          {currentSlide === 4 && (
                            <div className="space-y-4">
                              <div className="bg-primary-100 border border-primary-300 rounded-lg p-4 text-center">
                                <div className="text-lg font-bold text-primary-600">Professional</div>
                                <div className="text-2xl font-bold text-gray-900">$49.99</div>
                                <div className="text-sm text-gray-600">1,000 checks/month</div>
                              </div>
                              <div className="text-center">
                                <div className="text-sm text-gray-600">Usage: 150/1000</div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                  <div className="bg-primary-600 h-2 rounded-full" style={{width: '15%'}}></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Slider Indicators */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {dashboardPages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-primary-600 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Dashboard Feature Section */}
        <motion.div 
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Dashboard Mockup */}
          <motion.div 
            className="relative"
            variants={itemVariants}
          >
            
          </motion.div>


        </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
